import type { TaskPriority, TaskType } from "@/types/task";
import { dateLocale, withClock } from "@/lib/dateTime";

export interface QuickTask {
  title: string;
  task_type: TaskType;
  priority: TaskPriority;
  due_at: Date;
  /** What was understood, for the chips under the box */
  typeLabel: string;
  dueLabel: string;
  high: boolean;
  /** Whether the person said when; when not, the due time is a default */
  hasDate: boolean;
}

const TYPE_WORDS: [RegExp, TaskType, string][] = [
  [/\b(call|phone|ring)\b/i, "CALL", "Call"],
  [/\b(whatsapp|wa)\b/i, "WHATSAPP", "WhatsApp"],
  [/\b(e-?mail|mail|send .*(report|quote|invoice|proposal))\b/i, "EMAIL", "Email"],
  [/\b(meet|meeting|visit|demo|review)\b/i, "MEETING", "Meeting"],
];
const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

/** Time of day "3pm", "3:30 pm", "15:00", "at 10"; null when none */
function readTime(text: string): { h: number; m: number; matched: string } | null {
  let m = text.match(/\b(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i);
  if (m) {
    let h = Number(m[1]) % 12;
    if (m[3]!.toLowerCase() === "pm") h += 12;
    return { h, m: Number(m[2] || 0), matched: m[0] };
  }
  m = text.match(/\b(?:at\s+)?([01]?\d|2[0-3]):([0-5]\d)\b/);
  if (m) return { h: Number(m[1]), m: Number(m[2]), matched: m[0] };
  m = text.match(/\bat\s+(\d{1,2})\b/i);
  if (m) {
    const h = Number(m[1]);
    return { h: h >= 1 && h <= 6 ? h + 12 : h, m: 0, matched: m[0] };
  }
  return null;
}

/**
 * Turns "Call Meena about rayon prints tomorrow 3pm" into a task: a type from the verb, a due date and time
 * from the words for them, and the rest as the title. Understands today, tomorrow, weekday names, "next week",
 * "in 3 days", and "urgent" or "!" for high priority. Without a date the task is due today at 5 pm, or
 * tomorrow at 10 am when that has passed.
 */
export function parseQuickTask(input: string, now = new Date()): QuickTask | null {
  let text = input.trim().replace(/\s+/g, " ");
  if (!text) return null;

  const high = /(^|\s)(urgent|asap|high priority)\b|!$/i.test(text);
  text = text.replace(/(^|\s)(urgent|asap|high priority)\b/gi, " ").replace(/!+$/, "");

  const type = TYPE_WORDS.find(([re]) => re.test(text));
  const task_type: TaskType = type ? type[1] : "FOLLOW_UP";
  const typeLabel = type ? type[2] : "Follow-up";

  const due = new Date(now);
  due.setSeconds(0, 0);
  let hasDate = false;

  const strip = (re: RegExp) => {
    text = text.replace(re, " ").replace(/\s+/g, " ").trim();
  };

  const time = readTime(text);
  if (time) strip(new RegExp(time.matched.replace(/[.*+?^${}()|[\]\\]/g, "\\\\$&"), "i"));

  let dayOffset: number | null = null;
  const inDays = text.match(/\bin\s+(\d{1,2})\s+days?\b/i);
  const weekday = text.match(/\b(?:on\s+|next\s+)?(sun|mon|tue|wed|thu|fri|sat)[a-z]*\b/i);
  if (/\btomorrow\b/i.test(text)) (dayOffset = 1), strip(/\b(by\s+)?tomorrow\b/i);
  else if (/\btoday\b/i.test(text)) (dayOffset = 0), strip(/\b(by\s+)?today\b/i);
  else if (/\bnext week\b/i.test(text)) {
    dayOffset = ((1 - now.getDay() + 7) % 7) || 7;
    strip(/\bnext week\b/i);
  } else if (inDays) (dayOffset = Number(inDays[1])), strip(/\bin\s+\d{1,2}\s+days?\b/i);
  else if (weekday) {
    const target = DAYS.indexOf(weekday[1]!.toLowerCase());
    dayOffset = ((target - now.getDay() + 7) % 7) || 7;
    strip(/\b(?:on\s+|next\s+)?(sun|mon|tue|wed|thu|fri|sat)[a-z]*\b/i);
  }

  if (dayOffset !== null) {
    hasDate = true;
    due.setDate(due.getDate() + dayOffset);
    due.setHours(time ? time.h : 10, time ? time.m : 0, 0, 0);
  } else if (time) {
    hasDate = true;
    due.setHours(time.h, time.m, 0, 0);
    if (due.getTime() < now.getTime()) due.setDate(due.getDate() + 1);
  } else {
    due.setHours(17, 0, 0, 0);
    if (due.getTime() <= now.getTime()) {
      due.setDate(due.getDate() + 1);
      due.setHours(10, 0, 0, 0);
    }
  }

  text = text.replace(/\b(by|on|at)\s*$/i, "").trim();
  const title = (text || input.trim()).replace(/^./, (c) => c.toUpperCase());

  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const clock = due.toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }));
  const dueLabel = sameDay(due, now) ? `Today ${clock}` : sameDay(due, tomorrow) ? `Tomorrow ${clock}` : `${due.toLocaleDateString(dateLocale(), { weekday: "short", day: "numeric", month: "short" })} ${clock}`;

  return { title, task_type, priority: high ? "HIGH" : "NORMAL", due_at: due, typeLabel, dueLabel, high, hasDate };
}
